import { withArrival } from '../../lib/firstTouch.ts'
import { identifyVisitor } from '../../lib/telemetry.ts'
import { visitId } from '../../lib/visitId.ts'
import { api, json } from '../client.ts'
import { qk } from '../keys.ts'
import type { Teacher } from '../types.ts'

const DEVICE_KEY = 'tt-device'

/**
 * A random id this browser keeps, so a guest whose session cookie is lost is found again rather
 * than starting over empty. It only ever opens a guest; the api clears it once she has a password.
 */
function deviceId(): string {
	try {
		const known = localStorage.getItem(DEVICE_KEY)
		if (known) return known
		const fresh = randomId()
		localStorage.setItem(DEVICE_KEY, fresh)
		return fresh
	} catch {
		// Storage blocked (private mode on some browsers): a guest for this visit only.
		return randomId()
	}
}

/** The id this browser already keeps, if any. Unlike `deviceId` it never makes one. */
function knownDeviceId(): string | null {
	try {
		return localStorage.getItem(DEVICE_KEY)
	} catch {
		return null
	}
}

/** 128 random bits as hex. `crypto.randomUUID` only exists on https, and plain-http hosts exist. */
const randomId = () =>
	Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
		b.toString(16).padStart(2, '0'),
	).join('')

/**
 * The session lives in an httpOnly cookie, so this query -- not any client-side flag -- is the
 * single source of truth for who she is. Nobody has to sign up to start: with no session, she
 * becomes a guest here, silently. `null` only when even that failed, which sends her to sign in.
 * The request also says where the visit began, the one time anyone asks (lib/firstTouch.ts).
 */
async function becomeGuest(): Promise<Teacher | null> {
	// The visit exists before its first request, so the api's `guest_started` is in it like everything after.
	visitId()
	const id = deviceId()
	const guest = await withArrival((arrival) =>
		api.auth.guest.$post({ json: { deviceId: id, arrival } }),
	)
	if (!guest.ok) return null
	return (await json<{ teacher: Teacher }>(guest)).teacher
}

/**
 * The guest this browser had before its cookie was lost, found by the id it keeps, so the sign-in
 * page can still offer to bring her work along. Never makes one: nothing here is a start.
 */
async function findGuest(): Promise<Teacher | null> {
	const id = knownDeviceId()
	if (!id) return null
	const res = await api.auth.guest.$post({ json: { deviceId: id, find: true } })
	return res.ok ? (await json<{ teacher: Teacher }>(res)).teacher : null
}

/**
 * Who she is: the session's teacher or, with no session, a new guest (`guest`, the default) so she
 * can start at once. The sign-in and register pages pass `guest: false`: someone who came to sign
 * in has not started anything, and a guest made there is a throwaway row that never does a thing.
 * She may still be found: a guest whose cookie was lost. Nobody at all is `null`.
 */
export async function loadMe(guest: boolean): Promise<Teacher | null> {
	const res = await api.auth.me.$get()
	const teacher =
		res.status !== 401
			? (await json<{ teacher: Teacher }>(res)).teacher
			: guest
				? await becomeGuest()
				: await findGuest()
	if (teacher) identifyVisitor(teacher.id, teacher.guest)
	return teacher
}

/**
 * The question as a cached query. `guest: false` has its own key: "nobody" there is an answer with
 * data in it (null), so a refetch, on reconnect or on mount, never puts the page back to loading
 * and unmounts a form she is typing in. Fresh for 30 s, so the guard and the page it shows ask once,
 * not twice: each ask also spends the per-address guest limit a whole school shares. The app's own key (`guest`) never
 * holds a "nobody" the sign-in pages put there, so the page she goes to next still makes her a guest.
 */
export const meQuery = (guest: boolean) => ({
	queryKey: guest ? qk.me : qk.meNoGuest,
	retry: false,
	staleTime: guest ? 5 * 60_000 : 30_000,
	queryFn: () => loadMe(guest),
})
